// @ts-check
import {Button, Form, Space, Tabs, Input, Select} from "antd";
import {UserOutlined, LinkOutlined, FileTextOutlined} from "@ant-design/icons";
import {useState} from "react";
import {distActions} from "../../store";
import {useSelector, useDispatch} from "react-redux";

import {useTranslation} from "react-i18next";
import {useOrganization} from "../../context/organizationContext";

const {TextArea} = Input;

const PostDistributionForm = (props) => {
    const {t} = useTranslation();
    const [form] = Form.useForm();
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [postTextLength, setPostTextLength] = useState(0);
    // @ts-ignore - we use context for organization, but the linter suggests the organization is null
    const {organization} = useOrganization();

    // @ts-ignore - we use state from the store provider, but hte linter suggests the store is null
    const formState = useSelector((state) => state.dist.form);
    const dispatch = useDispatch();

    // method to select all employees from the button given the organization context
    const handleSelectAllEmployees = () => {
        const allIds = organization.employees.map((emp) => emp.id);
        setSelectedEmployees(allIds);
        form.setFieldsValue({employees: allIds});
        dispatch(distActions.setSelectedEmployees(organization.employees));
    };

    // method to clear all selected employees
    const handleClearEmployees = () => {
        setSelectedEmployees([]);
        form.setFieldsValue({employees: []});
        dispatch(distActions.setSelectedEmployees([]));
    };

    // method to select a single or multiple employees
    const handleChange = (values) => {
        const selected = organization.employees.filter((emp) => values.includes(emp.id));
        setSelectedEmployees(values);
        form.setFieldsValue({employees: values});
        dispatch(distActions.setSelectedEmployees(selected));
    };

    // handle post text change with character count
    const handlePostTextChange = (e) => {
        const value = e.target.value;
        setPostTextLength(value.length);
    };

    // if the organization is null, display the loading state (if for whatever reason the context provider failed)
    if (!organization || !Array.isArray(organization.employees)) {
        return <div>Загрузка данных организации...</div>;
    }

    return (
        <div>
            <Form
                form={form}
                layout="vertical"
                initialValues={formState}
                onValuesChange={(changed, allValues) => {
                    dispatch(distActions.updateForm(allValues));
                }}
            >
                <Form.Item
                    name="title"
                    label={t("postDistribution.postDistributionForm.title")}
                    rules={[{
                        required: true,
                        min: 2,
                        max: 200,
                        message: t("postDistribution.postDistributionForm.hint.title")
                    }]}
                >
                    <Input placeholder={t("postDistribution.postDistributionForm.placeholder.enterTitle")}/>
                </Form.Item>

                <Form.Item
                    name="description"
                    label={t("postDistribution.postDistributionForm.prompt")}
                    rules={[{
                        required: true,
                        min: 2,
                        max: 2000,
                        message: t("postDistribution.postDistributionForm.hint.prompt")
                    }]}
                >
                    <TextArea
                        rows={4}
                        placeholder={t("postDistribution.postDistributionForm.placeholder.enterPrompt")}
                        maxLength={2000}
                        onChange={(e) => {
                            dispatch(distActions.setFormValue({key: "instruction", value: e.target.value}));
                        }}
                    />
                </Form.Item>

                <Tabs
                    activeKey={formState.contentType === 0 ? "link" : "text"}
                    onChange={(key) =>
                        dispatch(distActions.setFormValue({key: "contentType", value: key === "link" ? 0 : 1}))
                    }
                    items={[
                        {
                            key: "link",
                            label: t("postDistribution.postDistributionForm.linkTab.title"),
                            icon: <LinkOutlined/>,
                            children: (
                                <Form.Item name="postLink"
                                           label={t("postDistribution.postDistributionForm.linkTab.label")}>
                                    <Input placeholder={t("postDistribution.postDistributionForm.linkTab.placeholder")}/>
                                </Form.Item>
                            ),
                        },
                        {
                            key: "text",
                            label: t("postDistribution.postDistributionForm.textTab.title"),
                            icon: <FileTextOutlined/>,
                            children: (
                                <div>
                                    <Form.Item name="postText" label={t("postDistribution.postDistributionForm.textTab.label")}>
                                        <TextArea
                                            rows={4}
                                            placeholder={t("postDistribution.postDistributionForm.textTab.placeholder")}
                                            maxLength={8000}
                                            onChange={handlePostTextChange}
                                        />
                                    </Form.Item>
                                    <div
                                        style={{
                                            textAlign: "right",
                                            fontSize: "12px",
                                            color: "#666",
                                            marginTop: "-16px",
                                            marginBottom: "16px",
                                        }}
                                    >
                                        {postTextLength}/8000
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />

                <Form.Item
                    name="employees"
                    label={t("postDistribution.postDistributionForm.employees.employeesLabel")}
                    rules={[{required: true, message: "Select at least one employee"}]}
                >
                    <div className="flex flex-col space-y-2">
                        <Select
                            mode="multiple"
                            placeholder={t("postDistribution.postDistributionForm.employees.placeholder")}
                            style={{width: "100%"}}
                            onChange={handleChange}
                            value={selectedEmployees}
                        >
                            {(organization?.employees || []).map((employee) => (
                                <Select.Option key={employee.id} value={employee.id}>
                                    {employee.fullName || `Employee ${employee.id}`}
                                </Select.Option>
                            ))}
                        </Select>
                        <Space>
                            <Button size="small" type="default" onClick={handleSelectAllEmployees}
                                    icon={<UserOutlined/>}>
                                {t("postDistribution.postDistributionForm.employees.selectAll")}
                            </Button>
                            <Button size="small" type="default" onClick={handleClearEmployees}>
                                {t("postDistribution.postDistributionForm.employees.clear")}
                            </Button>
                        </Space>
                    </div>
                </Form.Item>

                <Form.Item
                    name="languages"
                    label= {t("postDistribution.postDistributionForm.languageLabel")}
                    rules={[
                        {required: true, type: "array", min: 1, max: 3, message: t("postDistribution.postDistributionForm.hint.language")},
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder={t("postDistribution.postDistributionForm.placeholder.chooseLanguage")}
                        onChange={(values) => dispatch(distActions.setFormValue({key: "languages", value: values}))}
                        value={formState.languages}
                        style={{width: 300}}
                        maxTagCount={3}
                    >
                        <Select.Option value="ru">Русский</Select.Option>
                        <Select.Option value="kz">Қазақша</Select.Option>
                        <Select.Option value="en">English</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </div>
    );
};

export default PostDistributionForm;
